<template>
  <teleport to="body">
    <div class="modal-container" v-show="state.isVisible" :style="{ 'z-index': zIndex + 1 }">
      <div class="modal" :class="$attrs.class" ref="modalBlock">
        <div class="modal-title" ref="modalTitle">
          <div class="title-block">
            <div class="title">{{ state.titleResult }}</div>
          </div>
          <div class="modal__close" @click="close"></div>
          <slot name="before-content"></slot>
        </div>
        <div class="modal-content">
          <custom-scrollbar @scrollerComponent="initCustomScroller" class="custom-scrollbar">
            <component
              v-if="state.isVisible"
              :is="state.currentComponent"
              v-bind="state.currentComponentProps"
              @closeForm="close"
              @changeTitle="changeTitle"
              @setModalHeightAuto="setModalHeightAuto"></component>
          </custom-scrollbar>
        </div>
      </div>
    </div>

    <div
      class="modal-overlay background-fill"
      v-show="state.isVisible"
      @click="close"
      :style="{ 'z-index': zIndex }"></div>
  </teleport>
</template>

<script lang="ts" setup>
  import { debounce } from "lodash";
  import {
    defineOptions,
    defineExpose,
    defineProps,
    withDefaults,
    onMounted,
    onBeforeMount,
    reactive,
    nextTick,
    ref
  } from "vue";
  import CustomScrollbar from "@/components/_partials/CustomScrollbar.vue";
  import { useAuctionStore } from "@/stores/AuctionStore";

  defineOptions({
    inheritAttrs: false
  });

  const props = withDefaults(
    defineProps<{
      title?: string | null;
      dataComponent?: Record<any, any> | null;
      zIndex?: number;
    }>(),
    {
      title: null,
      dataComponent: null,
      zIndex: 12
    }
  );

  const state = reactive({
    isVisible: false,
    currentComponent: props.dataComponent,
    currentComponentProps: {},
    customScroll: null,
    modalWrapperHeight: null as number | null,
    modalHeight: null as number | null,
    titleResult: props.title
  });

  const auctionStore = useAuctionStore();

  const modalBlock = ref<HTMLDivElement | null>(null);
  const customScrollbarRef = ref<HTMLDivElement | null>(null);
  const modalTitle = ref<HTMLDivElement | null>(null);
  const resizeObserver = ref<ResizeObserver | null>(null);

  onMounted(() => {
    document.addEventListener("keydown", closeByEscapeKey);
    window.addEventListener("resize", debounce(windowResizeListener, 100));

    nextTick(() => {
      observeResize();
    });
  });

  onBeforeMount(() => {
    document.removeEventListener("keydown", closeByEscapeKey);
    window.removeEventListener("resize", windowResizeListener);
    unobserveResize();
  });

  const initCustomScroller = (refData) => {
    customScrollbarRef.value = refData.value.SimpleBar.el;
  };

  const open = () => {
    state.titleResult = props.title;
    state.isVisible = true;
    if (auctionStore.countOpenedModals < 1) {
      document.body.classList.add("overflow-hidden");
    }
    auctionStore.$patch({
      countOpenedModals: auctionStore.countOpenedModals + 1
    });
    changeModalContentHeight();
  };
  const close = () => {
    state.isVisible = false;
    customScrollbarRef.value.style.height = "";
    if (auctionStore.countOpenedModals <= 1) {
      document.body.classList.remove("overflow-hidden");
    }
    auctionStore.$patch({
      countOpenedModals: auctionStore.countOpenedModals - 1
    });
  };
  const closeByEscapeKey = (event: Event) => {
    if (event.keyCode === 27) {
      close();
    }
  };
  const toggleComponent = (component, props = {}) => {
    state.currentComponentProps = props;
    state.currentComponent = component;
  };
  const changeTitle = (titleNew: string) => {
    state.titleResult = titleNew;
  };
  const changeModalContentHeight = () => {
    getModalWrapperHeight();
    const modalScrollHeight = modalBlock.value.scrollHeight;

    if (modalScrollHeight > state.modalHeight) {
      customScrollbarRef.value.style.height = state.modalHeight - state.modalWrapperHeight + "px";
    }
  };
  const observeResize = () => {
    resizeObserver.value = new ResizeObserver((entries) => {
      for (let entry of entries) {
        state.modalHeight = entry.target.clientHeight;
        changeModalContentHeight();
      }
    });

    resizeObserver.value.observe(modalBlock.value);
  };
  const unobserveResize = () => {
    if (resizeObserver.value) {
      resizeObserver.value.disconnect();
      resizeObserver.value = null;
    }
  };
  const getModalWrapperHeight = () => {
    const modalTitleHeight = modalTitle.value.clientHeight;
    const paddingTop = parseInt(window.getComputedStyle(modalBlock.value, null).getPropertyValue("padding-top"));
    const paddingBottom = parseInt(window.getComputedStyle(modalBlock.value, null).getPropertyValue("padding-bottom"));

    state.modalWrapperHeight = modalTitleHeight + paddingTop + paddingBottom;
  };
  const windowResizeListener = () => {
    setModalHeightAuto();
    state.modalHeight = modalBlock.value?.clientHeight || 0;
    changeModalContentHeight();
  };
  const setModalHeightAuto = () => {
    customScrollbarRef.value.style.height = "auto";
  };

  defineExpose({
    toggleComponent,
    open,
    changeTitle
  });
</script>
